import type { Comment } from '@kizen/api/comment';
import type {
  CustomerNotificationIndicatorResponse,
  CustomerNotificationMetadata,
  CustomerNotificationParams,
  PaginatedCustomerNotificationList,
} from '@kizen/api/customer-notifications';
import type {
  DefaultError,
  InfiniteData,
  InfiniteQueryObserverOptions,
  QueryObserverOptions,
} from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type {
  Drop,
  WithInfiniteSelect,
  WithParams,
  WithRequired,
} from './types';

import {
  getCustomerNotifications,
  getCustomerNotificationComments,
  getCustomerNotificationIndicator,
  getCustomerNotificationMetadata,
} from '@kizen/api/customer-notifications';
import { getResource } from './resource';
import { curry1 } from './utils';

type CustomerNotificationsInfiniteQuery<
  TData = InfiniteData<PaginatedCustomerNotificationList, string | undefined>,
> = WithRequired<
  WithInfiniteSelect<
    InfiniteQueryObserverOptions<
      PaginatedCustomerNotificationList,
      DefaultError,
      TData,
      PaginatedCustomerNotificationList,
      [
        Resource<
          Readonly<['customer-notifications']>,
          CustomerNotificationParams
        >,
      ],
      string | undefined
    >
  >,
  'queryFn' | 'queryKey'
>;

type CustomerNotificationsInfiniteQueryOptions<TData> = WithParams<
  Omit<
    CustomerNotificationsInfiniteQuery<TData>,
    | 'queryFn'
    | 'queryKey'
    | 'getNextPageParam'
    | 'getPreviousPageParam'
    | 'initialPageParam'
  >,
  CustomerNotificationParams
>;

type CustomerNotificationsIndicatorQuery<TData> = WithRequired<
  QueryObserverOptions<
    CustomerNotificationIndicatorResponse,
    DefaultError,
    TData,
    CustomerNotificationIndicatorResponse,
    [Resource<Readonly<['customer-notifications', 'indicator']>>]
  >,
  'queryFn' | 'queryKey'
>;

type CustomerNotificationMetadataQuery<TData = CustomerNotificationMetadata> =
  WithRequired<
    QueryObserverOptions<
      CustomerNotificationMetadata,
      DefaultError,
      TData,
      CustomerNotificationMetadata,
      [Resource<Readonly<['customer-notifications', 'metadata']>>]
    >,
    'queryFn' | 'queryKey'
  >;

type CustomerNotificationCommentsQuery<TData = Comment[]> = WithRequired<
  QueryObserverOptions<
    Comment[],
    DefaultError,
    TData,
    Comment[],
    [Resource<Readonly<['customer-notifications', string, 'comments']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const customerNotificationCommentsQuery = <TData = Comment[]>(
  axios: AxiosInstance,
  notificationId: string,
  options: Omit<
    CustomerNotificationCommentsQuery<TData>,
    'queryFn' | 'queryKey'
  > = {}
): CustomerNotificationCommentsQuery<TData> => {
  const path = ['customer-notifications', notificationId, 'comments'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;

      return getCustomerNotificationComments(axios, notificationId, { signal });
    },
    ...options,
  };
};

export const customerNotificationsInfiniteQuery = <
  TData = PaginatedCustomerNotificationList,
>(
  axios: AxiosInstance,
  options: CustomerNotificationsInfiniteQueryOptions<TData> = {}
): CustomerNotificationsInfiniteQuery<TData> => {
  const path = ['customer-notifications'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { infinite: true, params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        pageParam,
        queryKey: [qk],
        signal,
      } = context;

      const p = pageParam ? { ...qk.params, cursor: pageParam } : qk.params;

      return getCustomerNotifications(axios, { signal, params: p });
    },
    getNextPageParam: (page) => page.next_cursor,
    getPreviousPageParam: (page) => page.prev_cursor,
    initialPageParam: undefined,
    ...queryOpts,
  };
};

export const customerNotificationsIndicatorQuery = <
  TData = CustomerNotificationIndicatorResponse,
>(
  axios: AxiosInstance,
  options: Omit<
    CustomerNotificationsIndicatorQuery<TData>,
    'queryKey' | 'queryFn'
  > = {}
): CustomerNotificationsIndicatorQuery<TData> => {
  const path = ['customer-notifications', 'indicator'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;

      return getCustomerNotificationIndicator(axios, { signal });
    },
    ...options,
  };
};

export const customerNotificationsMetadataQuery = <
  TData = CustomerNotificationMetadata,
>(
  axios: AxiosInstance,
  options: Omit<
    CustomerNotificationMetadataQuery<TData>,
    'queryFn' | 'queryKey'
  >
): CustomerNotificationMetadataQuery<TData> => {
  const path = ['customer-notifications', 'metadata'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;

      return getCustomerNotificationMetadata(axios, { signal });
    },
    ...options,
  };
};

class Queries {
  public customerNotificationCommentsQuery: <TData = Comment[]>(
    ...args: Drop<Parameters<typeof customerNotificationCommentsQuery<TData>>>
  ) => ReturnType<typeof customerNotificationCommentsQuery<TData>>;
  public customerNotificationsInfiniteQuery: <
    TData = InfiniteData<PaginatedCustomerNotificationList, string | undefined>,
  >(
    ...args: Drop<Parameters<typeof customerNotificationsInfiniteQuery<TData>>>
  ) => ReturnType<typeof customerNotificationsInfiniteQuery<TData>>;
  public customerNotificationsIndicatorQuery: <
    TData = CustomerNotificationIndicatorResponse,
  >(
    ...args: Drop<Parameters<typeof customerNotificationsIndicatorQuery<TData>>>
  ) => ReturnType<typeof customerNotificationsIndicatorQuery<TData>>;
  public customerNotificationsMetadataQuery: <
    TData = CustomerNotificationMetadata,
  >(
    ...args: Drop<Parameters<typeof customerNotificationsMetadataQuery<TData>>>
  ) => ReturnType<typeof customerNotificationsMetadataQuery<TData>>;

  constructor(axios: AxiosInstance) {
    this.customerNotificationCommentsQuery = curry1(
      customerNotificationCommentsQuery
    )(axios);
    this.customerNotificationsInfiniteQuery = curry1(
      customerNotificationsInfiniteQuery
    )(axios);
    this.customerNotificationsIndicatorQuery = curry1(
      customerNotificationsIndicatorQuery
    )(axios);
    this.customerNotificationsMetadataQuery = curry1(
      customerNotificationsMetadataQuery
    )(axios);
  }
}

export type CustomerNotificationsQueries = Queries;

export const defCustomerNotificationsQueries = (
  axios: AxiosInstance
): CustomerNotificationsQueries => {
  return new Queries(axios);
};
